import { inject } from '@angular/core'
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlSerializer, UrlTree, CanActivateFn } from '@angular/router'
import { AuthService } from '@app/modules/auth/auth.service'
import { merge, Observable, of } from 'rxjs'
import { filter, first, map, switchMap } from 'rxjs/operators'
import { negativeBoolean } from '@core/helpers/negative-boolean'

export const adminGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService)
  const urlSerializer = inject(UrlSerializer)

  const isAdmin$ = authService.currentUser$.pipe(map((user) => user.isAdmin()))
  const canActivate$ = isAdmin$.pipe(filter((isAdmin) => !!isAdmin))
  const cannotActivate$ = isAdmin$.pipe(
    filter(negativeBoolean),
    switchMap(() => of(urlSerializer.parse('/'))),
  )
  return merge(canActivate$, cannotActivate$).pipe(first())
}

export const clientGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService)
  const urlSerializer = inject(UrlSerializer)

  return authService.currentUser$.pipe(
    first(),
    map((user) => {
      if (!user) {
        const encodedUrl = encodeURIComponent(state.url)
        return urlSerializer.parse(`/login?redirect=${encodedUrl}`)
      }

      if (!user.isClient()) {
        return urlSerializer.parse('/')
      }

      return true
    }),
  )
}

export const resellerGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService)
  const urlSerializer = inject(UrlSerializer)

  const isReseller$ = authService.currentUser$.pipe(map((user) => user.isReseller()))
  const canActivate$ = isReseller$.pipe(filter((isReseller) => !!isReseller))
  const cannotActivate$ = isReseller$.pipe(
    filter(negativeBoolean),
    switchMap(() => of(urlSerializer.parse('/'))),
  )
  return merge(canActivate$, cannotActivate$).pipe(first())
}
